/**
 * SetupGuide — the client-facing gate that must reach "ready" before the
 * guided tour starts (live, non-demo accounts only).
 * Steps: add logo · connect accounting · set up team (+ email staff) · learn Corvana.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  CheckCircle2, Circle, Image as ImageIcon, Plug, Users2, Sparkles,
  ArrowRight, Loader2, Mail, BarChart3, Target, TrendingUp,
} from 'lucide-react';
import { toast } from '@/utils/safeToast';
import confetti from 'canvas-confetti';
import { authFetch } from '@/utils/authFetch';
import { API_URL } from '@/config/apiConfig';
import { useAuth } from '@/contexts/AuthContext';
import { getIndustryTerms } from '@/config/industryTerminology';

const STEP_ICON = { logo: ImageIcon, modules: Plug, staff: Users2, brand: Sparkles };

const greetingFor = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
};

export default function SetupGuide({ open = true, onReady, onClose, onStatusChange }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [showBrand, setShowBrand] = useState(false);

  const load = useCallback(async () => {
    // Retry through the transient auth-init / cookie-drop races the preview
    // harness produces right after login, so the steps reliably populate.
    for (let attempt = 0; attempt < 4; attempt += 1) {
      try {
        const r = await authFetch(`${API_URL}/api/account/setup-status`);
        if (r.ok) { const d = await r.json(); setStatus(d); onStatusChange?.(d); setLoading(false); return; }
      } catch { /* transient — retry */ }
      await new Promise((resolve) => setTimeout(resolve, 700));
    }
    setLoading(false);
  }, [onStatusChange]);

  useEffect(() => { load(); }, [load]);

  const ack = async (step) => {
    setBusy(step);
    try {
      await authFetch(`${API_URL}/api/account/setup-status/ack`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ step }),
      });
      await load();
    } catch { toast.error('Could not save'); }
    setBusy('');
  };

  const nudgeStaff = async () => {
    setBusy('staff');
    try {
      const r = await authFetch(`${API_URL}/api/staff/roster/nudge-incomplete`, { method: 'POST' });
      if (r.ok) {
        const d = await r.json();
        if (d.nudged_count > 0) toast.success(`Setup email sent to ${d.nudged_count} teammate${d.nudged_count === 1 ? '' : 's'}`);
        else toast.info('No teammates are awaiting setup — add some on the Team page first');
      }
      await load();
    } catch { toast.error('Could not email your team'); }
    setBusy('');
  };

  const goto = (path) => { onClose?.(); navigate(path); };

  const celebrate = () => {
    const fire = (ratio, opts) => confetti({
      ...opts, origin: { y: 0.7 }, zIndex: 9999,
      particleCount: Math.floor(200 * ratio),
      colors: ['#14b8a6', '#3b82f6', '#22d3ee', '#a78bfa', '#f59e0b'],
    });
    fire(0.25, { spread: 26, startVelocity: 55 });
    fire(0.2, { spread: 60 });
    fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
    fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
    fire(0.1, { spread: 120, startVelocity: 45 });
    toast.success(user?.first_name ? `You’re all set, ${user.first_name}!` : 'You’re all set!');
    setTimeout(() => onReady?.(), 650);
  };

  const ready = status?.ready;
  const steps = status?.steps || [];
  const staffMeta = steps.find((s) => s.id === 'staff')?.meta || {};

  // Personalisation — make the guide feel made for this person & business.
  const firstName = user?.first_name || 'there';
  const company = status?.company_name || user?.company_name || 'your business';
  const terms = getIndustryTerms(user?.industry);
  const industryLabel = (user?.industry || '').toString().replace(/_/g, ' ').trim() || 'your industry';
  const revenueLower = terms.revenue.toLowerCase();
  const customersLower = terms.customersLower;
  const teamLower = `${terms.employee.toLowerCase()}s`;
  const brandPoints = [
    { Icon: BarChart3, t: 'Live numbers', d: `Your ${revenueLower} and ${customersLower}, turned into dashboards you can actually read.` },
    { Icon: TrendingUp, t: 'Industry benchmarks', d: `See how ${company} compares with other ${industryLabel} businesses — backed by trusted sources.` },
    { Icon: Target, t: 'Game plans', d: `Every insight comes with a roadmap you can hand straight to your ${teamLower}.` },
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto p-0 overflow-hidden" data-testid="setup-guide">
        {/* Brand hero */}
        <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-8 py-7 text-center">
          <div className="inline-block bg-white/10 border border-white/15 text-slate-300 text-[11px] tracking-widest uppercase px-3 py-1 rounded-full mb-3" data-testid="setup-greeting">
            {greetingFor()}, {firstName}
          </div>
          <DialogHeader>
            <DialogTitle className="text-white text-2xl font-bold tracking-tight" data-testid="setup-title">Let’s get {company} ready</DialogTitle>
            <DialogDescription className="text-slate-300 mt-1">
              A few quick steps and Corvana turns your {revenueLower} and {customersLower} into clear decisions — then we’ll show you around.
            </DialogDescription>
          </DialogHeader>
          {status && (
            <div className="mt-5 max-w-sm mx-auto">
              <div className="flex justify-between text-[11px] text-slate-400 mb-1.5">
                <span>{status.completed} of {status.total} done</span>
                <span>{status.completion_pct}%</span>
              </div>
              <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-teal-400 to-blue-500 transition-all duration-700" style={{ width: `${status.completion_pct}%` }} data-testid="setup-progress" />
              </div>
            </div>
          )}
        </div>

        <div className="p-6 space-y-3">
          {loading ? (
            <div className="flex justify-center py-10 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : (
            steps.map((step) => {
              const Icon = STEP_ICON[step.id] || Circle;
              return (
                <div key={step.id} className={`rounded-xl border p-4 transition-colors ${step.done ? 'border-emerald-200 bg-emerald-50/50' : 'border-slate-200'}`} data-testid={`setup-step-${step.id}`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${step.done ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-500'}`}>
                      {step.done ? <CheckCircle2 className="w-5 h-5" data-testid={`setup-step-${step.id}-done`} /> : <Icon className="w-5 h-5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900">{step.label}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{step.hint}</p>

                      {/* Per-step actions */}
                      {!step.done && step.id === 'logo' && (
                        <Button size="sm" variant="outline" className="mt-2.5" onClick={() => goto('/app/profile')} data-testid="setup-action-logo">Add logo <ArrowRight className="w-3 h-3 ml-1" /></Button>
                      )}
                      {!step.done && step.id === 'modules' && (
                        <Button size="sm" variant="outline" className="mt-2.5" onClick={() => goto('/app/integrations/accounting')} data-testid="setup-action-modules">Connect accounting <ArrowRight className="w-3 h-3 ml-1" /></Button>
                      )}
                      {step.id === 'staff' && !step.done && (
                        <div className="mt-2.5 flex flex-wrap gap-2 items-center">
                          <Button size="sm" variant="outline" onClick={() => goto('/app/team')} data-testid="setup-action-staff">Manage team <ArrowRight className="w-3 h-3 ml-1" /></Button>
                          <Button size="sm" onClick={nudgeStaff} disabled={busy === 'staff'} className="bg-gradient-to-r from-teal-500 to-blue-600 text-white" data-testid="setup-action-email-staff">
                            {busy === 'staff' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Mail className="w-3 h-3 mr-1" />}
                            Email staff to set up their profile
                          </Button>
                          <button onClick={() => ack('staff')} className="text-xs text-slate-400 hover:text-slate-600 underline" data-testid="setup-action-staff-skip">It’s just me</button>
                          {(staffMeta.staff_total > 0) && <span className="text-[11px] text-slate-400 w-full">{staffMeta.staff_invited}/{staffMeta.staff_total} invited · {staffMeta.staff_linked} active</span>}
                        </div>
                      )}
                      {step.id === 'brand' && !step.done && (
                        <div className="mt-2.5">
                          {!showBrand ? (
                            <Button size="sm" variant="outline" onClick={() => setShowBrand(true)} data-testid="setup-action-brand">Take a look</Button>
                          ) : (
                            <div className="rounded-lg bg-slate-50 border border-slate-100 p-3 space-y-2" data-testid="setup-brand-overview">
                              {brandPoints.map((x) => (
                                <div key={x.t} className="flex items-start gap-2.5">
                                  <x.Icon className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                                  <p className="text-xs text-slate-600"><span className="font-semibold text-slate-800">{x.t}.</span> {x.d}</p>
                                </div>
                              ))}
                              <Button size="sm" onClick={() => ack('brand')} disabled={busy === 'brand'} className="mt-1" data-testid="setup-action-brand-ack">
                                {busy === 'brand' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null} Got it
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex items-center justify-between gap-3">
          <div className="flex items-center gap-4">
            <button onClick={load} className="text-xs text-slate-400 hover:text-slate-600" data-testid="setup-refresh">Refresh status</button>
            <button onClick={() => onClose?.()} className="text-xs text-slate-400 hover:text-slate-600 underline" data-testid="setup-finish-later">I’ll finish later</button>
          </div>
          <Button
            disabled={!ready}
            onClick={celebrate}
            data-testid="setup-start-tour"
            className={ready ? 'bg-gradient-to-r from-teal-500 to-blue-600 text-white' : ''}
          >
            {ready ? <>Start your tour <ArrowRight className="w-4 h-4 ml-1" /></> : 'Complete the steps above to continue'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
